import { RequestOpenAxios } from '../features/access/APIUtils';
import { formatCategoryName } from '../utils/categoryFormatter';
import categoriesData from '../backendshared/xnews_categories.json';

// Define the translation data structure locally to avoid circular import
export interface TranslationType {
  navigations: {
    header?: {
      home?: string;
      news?: string;
      entertainment?: string;
      sports?: string;
      signIn?: string;
      back?: string;
      archive?: string;
      archiveDescription?: string;
      politics?: string;
      cryptocurrency?: string;
      religion?: string;
      war?: string;
      finance?: string;
      crime?: string;
      history?: string;
      weather?: string;
      economics?: string;
      infrastructure?: string;
      immigration?: string;
      automotive?: string;
      space?: string;
      trade?: string;
      health?: string;
      music?: string;
      film?: string;
      streaming?: string;
      technology?: string;
      gaming?: string;
      celebrity?: string;
      racing?: string;
      basketball?: string;
      wrestling?: string;
      football?: string;
      baseball?: string;
      athletics?: string;
      cricket?: string;
      boxing?: string;
      cycling?: string;
      mma?: string;
      horseracing?: string;
      hockey?: string;
      soccer?: string;
      rugby?: string;
      esports?: string;
      golf?: string;
      [key: string]: string | undefined;
    };
    footer?: {
      copyright?: string;
      terms?: string;
      privacy?: string;
      followUs?: string;
      subscribe?: string;
      about?: string;
      contact?: string;
      [key: string]: string | undefined;
    };
    [key: string]: any;
  };
  privacy?: {
    title?: string;
    lastUpdated?: string;
    introduction?: string;
    collectionTitle?: string;
    collectionDesc?: string;
    automaticTitle?: string;
    automaticDesc?: string;
    usageTitle?: string;
    usageList?: string[];
    sharingTitle?: string;
    sharingDesc?: string;
    cookiesTitle?: string;
    cookiesDesc?: string;
    thirdPartyTitle?: string;
    thirdPartyDesc?: string;
    childrenTitle?: string;
    childrenDesc?: string;
    securityTitle?: string;
    securityDesc?: string;
    changesTitle?: string;
    changesDesc?: string;
    contactTitle?: string;
    contactDesc?: string;
    [key: string]: any;
  };
  terms?: {
    title?: string;
    lastUpdated?: string;
    introduction?: string;
    acceptanceTitle?: string;
    acceptanceDesc?: string;
    changesTitle?: string;
    changesDesc?: string;
    userAccountTitle?: string;
    userAccountDesc?: string;
    contentTitle?: string;
    contentDesc?: string;
    userContentTitle?: string;
    userContentDesc?: string;
    prohibitedTitle?: string;
    prohibitedList?: string[];
    disclaimerTitle?: string;
    disclaimerDesc?: string;
    limitationTitle?: string;
    limitationDesc?: string;
    governingLawTitle?: string;
    governingLawDesc?: string;
    contactTitle?: string;
    contactDesc?: string;
    [key: string]: any;
  };
  [key: string]: any;
}

// Function to normalize language code for consistency
export const normalizeLanguage = (lang: string): string => {
  if (!lang) return 'en';
  
  const langLower = lang.toLowerCase();
  
  if ( langLower === 'cn') {
    return 'cn';
  } else if (langLower === 'tw') {
    return 'tw';
  } else if (langLower.startsWith('en')) {
    return 'en';
  } else if (langLower.startsWith('es')) {
    return 'es';
  } else if (langLower.startsWith('fr')) {
    return 'fr';
  }
  
  return 'en';
};

// Set preferred language via API
export const setPreferredLanguage = async (
  locale: string
): Promise<{ status: string; message?: string }> => {
  try {
    localStorage.setItem('preferredLanguage', locale);
    return { status: 'success' };
  } catch (error) {
    console.error('[LOCALES] Failed to set preferred language:', error);
    return { status: 'error', message: 'Failed to store preferred language' };
  }
};


// Fetch navigation translations for a specific language
export const fetchNavigationTranslationsForLanguage = async (
  locale: string
): Promise<TranslationType> => {
  try {
    console.log(`[API] Fetching translations for ${locale}`);

    // Construct filename based on your naming pattern
    const filename = `messages_${locale}.json`;

    const response = await fetch(`/locales/${filename}`);

    if (!response.ok) {
      throw new Error(`Failed to fetch translations for ${locale}`);
    }

    const data = await response.json();

    const navigations = data.navigations
      ? data.navigations
      : {
          header: data.header ?? {},
          footer: data.footer ?? {},
        };

    const processedData: TranslationType = {
      navigations,
    };

    const promoteProperty = (propName: 'privacy' | 'terms') => {
      if (data[propName]) {
        processedData[propName] = data[propName];
        console.log(`[API] Using ${propName} from root level`);
      } else if (navigations[propName]) {
        processedData[propName] = navigations[propName];
        console.log(`[API] Promoting ${propName} from navigations to root level`);
      }
    };

    promoteProperty('privacy');
    promoteProperty('terms');

    return processedData;
  } catch (error) {
    console.error(`[API] Error fetching translations for ${locale}:`, error);
    return getFallbackTranslations(locale);
  }
};






// Function to generate translations for all categories
const generateCategoryTranslations = () => {
  const translations: Record<string, string> = {};
  
  // Process each category type
  Object.entries(categoriesData).forEach(([categoryType, subcategories]: [string, string[]]) => {
    // Add the category type translation
    translations[categoryType.toLowerCase()] = formatCategoryName(categoryType);
    
    // Add subcategory translations
    subcategories.forEach(subcategory => {
      translations[subcategory.toLowerCase()] = formatCategoryName(subcategory);
    });
  });
  
  return translations;
};

// Provide fallback translations for common languages
const getFallbackTranslations = (locale: string): TranslationType => {
  console.log(`[API] Using fallback translations for ${locale} - this indicates the API request failed or returned invalid data`);
  
  // Generate translations for all categories
  const categoryTranslations = generateCategoryTranslations();
  
  // Default fallback
  return {
    navigations: {
      header: {
        // Main navigation
        home: "Home",
        news: "News",
        entertainment: "Entertainment",
        sports: "Sports",
        signIn: "Sign In",
        back: "Back",
        archive: "Archive",
        archiveDescription: "Browse news by category and subcategory for this date",

        // Add all generated category translations
        ...categoryTranslations,

        // Other categories
        more: "More",
        all: "All"
      },
      footer: {
        copyright: "© 2024 Global24. All rights reserved.",
        about: "About Us",
        contact: "Contact",
        terms: "Terms of Service",
        privacy: "Privacy Policy",
        followUs: "Follow Us",
        subscribe: "Subscribe to Newsletter"
      }
    },
    privacy: {
      title: "Privacy Policy",
      lastUpdated: "Last Updated: May 3, 2025",
      introduction: "At Global24, we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website.",
      collectionTitle: "Information We Collect",
      collectionDesc: "We may collect personal information that you voluntarily provide when interacting with our website, including but not limited to your name, email address, and any other information you choose to provide when registering for an account, subscribing to our newsletter, or contacting us.",
      automaticTitle: "Information Automatically Collected",
      automaticDesc: "When you visit our website, we may automatically collect certain information about your device, including your IP address, browser type, operating system, and browsing patterns. We use cookies and similar tracking technologies to enhance your user experience and analyze website traffic.",
      usageTitle: "How We Use Your Information",
      usageList: [
        "To provide and maintain our website",
        "To personalize your experience",
        "To improve our website and services",
        "To send periodic emails and newsletters",
        "To process transactions",
        "To respond to your inquiries"
      ],
      sharingTitle: "Disclosure of Your Information",
      sharingDesc: "We do not sell, trade, or otherwise transfer your personally identifiable information to third parties without your consent, except as described in this Privacy Policy.",
      cookiesTitle: "Cookies Policy",
      cookiesDesc: "We use cookies to enhance your browsing experience, analyze website traffic, and personalize content. You can choose to have your computer warn you each time a cookie is being sent, or you can choose to turn off all cookies through your browser settings.",
      thirdPartyTitle: "Third-Party Links",
      thirdPartyDesc: "Our website may contain links to third-party websites that are not operated by us. We have no control over and assume no responsibility for the content, privacy policies, or practices of any third-party sites or services.",
      childrenTitle: "Children's Privacy",
      childrenDesc: "Our website is not intended for individuals under the age of 13. We do not knowingly collect personal information from children under 13.",
      securityTitle: "Security of Your Information",
      securityDesc: "We use administrative, technical, and physical security measures to help protect your personal information from unauthorized access, use, or disclosure.",
      changesTitle: "Changes to This Privacy Policy",
      changesDesc: "We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the 'Last Updated' date.",
      contactTitle: "Contact Information",
      contactDesc: "If you have any questions or concerns about this Privacy Policy, please contact us at privacy@global24.news."
    },
    terms: {
      title: "Terms of Service",
      lastUpdated: "Last Updated: May 3, 2025",
      introduction: "Welcome to Global24. By accessing or using our website, you agree to be bound by these Terms of Service and all applicable laws and regulations.",
      acceptanceTitle: "Acceptance of Terms",
      acceptanceDesc: "By accessing or using Global24, you agree to be bound by these Terms of Service. If you do not agree to all of these terms, you may not use our services.",
      changesTitle: "Changes to Terms",
      changesDesc: "We may revise these terms at any time without notice. By continuing to use our website after any changes, you accept the revised terms.",
      userAccountTitle: "User Accounts",
      userAccountDesc: "When you create an account with us, you must provide accurate and complete information. You are responsible for maintaining the security of your account, and you are fully responsible for all activities that occur under your account.",
      contentTitle: "Content and Intellectual Property",
      contentDesc: "All content on our website, including text, graphics, logos, images, and software, is the property of Global24 or its content suppliers and is protected by international copyright laws.",
      userContentTitle: "User-Generated Content",
      userContentDesc: "By posting content on our site, you grant us a non-exclusive, royalty-free, perpetual, and worldwide license to use, reproduce, modify, and distribute your content in connection with our services.",
      prohibitedTitle: "Prohibited Activities",
      prohibitedList: [
        "Using our service for any illegal purpose",
        "Violating any laws in your jurisdiction",
        "Interfering with or disrupting our services",
        "Impersonating any person or entity",
        "Harvesting or collecting user information without permission",
        "Uploading harmful code or malware"
      ],
      disclaimerTitle: "Disclaimer of Warranties",
      disclaimerDesc: "Our website is provided 'as is' without any warranties, expressed or implied. We do not guarantee that our services will be error-free, secure, or continuously available.",
      limitationTitle: "Limitation of Liability",
      limitationDesc: "Global24 shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of or inability to use our services.",
      governingLawTitle: "Governing Law",
      governingLawDesc: "These Terms shall be governed by the laws of the United States, without regard to its conflict of law provisions.",
      contactTitle: "Contact Information",
      contactDesc: "If you have any questions about these Terms, please contact us at terms@global24.news."
    }
  };
}; 